/**
 * RowMentions: the clients named on this care manager's call, under the
 * note on that row. Small chips (MentionChip) that wrap to at most two
 * lines; past that, "+N more", which opens the weekly call dialog where
 * every mention is listed (for a seat without the dialog, the words alone,
 * with the rest of the names in the tooltip). At most
 * ROW_MENTION_CHIPS_MAX chips show; where the browser lays them out and
 * two lines hold fewer, it shows fewer, so the row never grows a third
 * line. Mount it keyed on the mentions so a new set starts over.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { TextAction } from '@/components/workspace/TextAction';
import { MentionChip } from '@/components/capacity/MentionChip';
import { ROW_MENTION_CHIPS_MAX } from '@/lib/callMentions';
import type { CallMentionWire } from '@/lib/capacityApi';

/** Two chip lines: each 1.25rem (leading-4 plus its padding) and the
 *  0.25rem gap between them. */
const TWO_LINES = 'max-h-[2.75rem]';

export function RowMentions({
  rosterId,
  mentions,
  onMore,
}: {
  rosterId: string;
  mentions: CallMentionWire[];
  /** Opens the weekly call dialog; null for a seat without it. */
  onMore: (() => void) | null;
}): ReactNode {
  const [shown, setShown] = useState(Math.min(mentions.length, ROW_MENTION_CHIPS_MAX));
  const box = useRef<HTMLDivElement | null>(null);

  // Where there is layout, drop a chip at a time until two lines hold the
  // chips and the "+N more".
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || el.clientHeight === 0) return;
    if (el.scrollHeight > el.clientHeight + 1 && shown > 1) setShown(shown - 1);
  }, [shown]);

  if (mentions.length === 0) return null;
  const rest = mentions.length - shown;
  const more = `+${rest} more`;

  return (
    <div
      ref={box}
      data-testid={`availability-mentions-${rosterId}`}
      className={`mt-1.5 flex flex-wrap items-center gap-1 overflow-hidden ${TWO_LINES}`}
    >
      {mentions.slice(0, shown).map((m) => (
        // flex, so the wrapper is the chip's own height, not the cell's line.
        <span key={m.client_id} className="flex min-w-0 max-w-full">
          <MentionChip name={m.display_name} tag={m.tag} />
        </span>
      ))}
      {rest > 0 &&
        (onMore ? (
          <TextAction className="!py-0" data-testid={`availability-mentions-more-${rosterId}`} onClick={onMore}>
            {more}
          </TextAction>
        ) : (
          <span
            className="px-1 text-xs font-semibold text-ink-soft"
            data-testid={`availability-mentions-more-${rosterId}`}
            title={mentions
              .slice(shown)
              .map((m) => m.display_name)
              .join(', ')}
          >
            {more}
          </span>
        ))}
    </div>
  );
}
